// 氏名の表記・整形ユーティリティ (spec 2026-07-18 D22 + カタカナ表示 D23)。純粋関数のみ (I/O なし)。
// LLM 抽出 (extractName) が primary で、これはその静的フォールバック + 表示整形に使う。
//
// 表示は **カタカナのみ** に統一する: 漢字は読み方が確定しない限り出さない
// (中韓読み再発を入口で塞ぐ + 姓の読み違いによる被験者の不快感を避ける、D23)。

// CJK 統合漢字 (基本 + 拡張A) を含むか。テスト・呼び出し側の防衛ガードで使う。
export function hasKanji(s: string): boolean {
  return /[一-龯㐀-䶿]/.test(s);
}

// ひらがな (U+3041〜U+3096) をカタカナ (U+30A1〜U+30F6) に変換。オフセットは +0x60。
// カタカナ・漢字・ラテン文字などは触らない。
export function toKatakana(s: string): string {
  return s.replace(/[ぁ-ゖ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60));
}

// 漢字 (CJK 統合基本 + 拡張A) を除去。「表示にはカタカナしか残さない」ための最後の関門。
export function stripKanji(s: string): string {
  return s.replace(/[一-龯㐀-䶿]/g, "");
}

// LLM 失敗時の静的フォールバック: 自己紹介発話の定型 (接頭語・語尾) を落とす。
// **保守的に** — 過剰除去で氏名を壊すより取りこぼす方を選ぶ (LLM が primary、
// 復唱確認が最終ゲート)。剥がすのは (a) コピュラ「です/でした」、(b) 漢字動詞を含む
// 「と申します/と申しまして/と申す/と言います/と言う」、(c) 接頭語「私は/名前は」等 のみ。
// **単独の「と」を末尾から剥がしてはならない** — 「山本(やまもと)」「橋本」等 と で終わる
// 姓を壊すため (回帰テスト参照)。かな単独の「といいます/になります」も、STT が
// 助詞「と」を名前と融合させたときに名前末尾の と を食う恐れがあるので剥がさない。
const LEADING = /^(?:私|わたし|わたくし|僕|ぼく)(?:は|が)|^(?:お)?名前(?:は|が)/;
const TRAILING = /(?:と申しまして|と申します|と申す|と言います|と言う|でした|です)$/;

export function stripNameAffixes(raw: string): string {
  let s = raw.trim().replace(/[。、．，\s]+$/u, "");
  s = s.replace(LEADING, "");
  s = s.replace(TRAILING, "");
  return s.trim();
}

// 表示・保存・復唱用に「カタカナのみ」の氏名を作る (D23)。
// 優先度: reading (LLM が返した読み) > name (raw 氏名)。
//
// **語尾除去はこの関数が持ち、必ず toKatakana の前に当てる (D25-8)。**
// LEADING / TRAILING はひらがな・漢字で書かれているため、カタカナ化の後に
// 当てても「です」は「デス」になっていて一致しない。以前は呼び出し側が name に
// だけ除去を当てており、reading が優先されると素通りしていた
// (formatName("山田太郎","やまだたろうです") = "ヤマダタロウデス")。
// 順序依存と適用漏れをまとめてここに閉じ込める。
//
// source を選ぶ前に両方へ除去を当てるので、reading が語尾だけ (「です」) の
// 縮退ケースでは空になって name 側に倒れる。空文字が返ったら呼び出し側は
// 「表示できない」として再認識に倒す (NameCapture.resolveName / runCycle 参照)。
//
// **内部の空白も落とす (spec 2026-08-20 D12)。** 理由は 2 つあり、どちらも実測。
// (a) LLM は姓名の間に空白を入れることがある (「カトウ シオリ」、16 件中 5 件)。
//     stripNameAffixes が落とすのは **末尾** の空白だけなので、そのままだと
//     displayName に空白が残り TTS もそこで切る。
// (b) **matchKey は空白を畳まない** — 「カトウ シオリ」と「カトウシオリ」は別の鍵に
//     なるので、同じ氏名が 2 つの候補として並び **本人に同じ名前を 2 回聞かせる**。
// JS の \s は全角空白 (U+3000) を含むので、これ 1 本で半角・全角の両方が落ちる。
// 除去は語尾除去の **後** で、末尾の trim も兼ねる (\s+ が前後の空白も食う)。
// キーボード入力 (前提 spec の D21) も同じ関数を通るので、そちらの空白も落ちる
// (意図した副作用 — 声とキーボードで displayName の形が揃う)。
export function formatName(name: string, reading: string): string {
  const source = stripNameAffixes(reading) || stripNameAffixes(name);
  return stripKanji(toKatakana(source)).replace(/\s+/gu, "");
}
